import React from 'react';
import { compose } from 'redux';
import { LocalizedStandardDate, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _map from 'lodash/map';
import _round from 'lodash/round';
import PropTypes from 'prop-types';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
// Color
import { statusPalette } from 'components/core/colors';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mDevice from 'models/device';
import mInstallation from 'models/installation';
// Custom
import { TABLE_ROW_HEIGHT } from 'constants/App';
import withScroll from 'hocs/WithScroll';
import { listRowSpecsShape, installationDataShape } from 'constants/PropTypesShapes';
import { showPath } from 'installations/navigation';
import DeviceWidget from 'devices/assets/main/DeviceWidget';
import withLoading from 'hocs/WithLoading';
import { EditIconButton, DeleteIconButton } from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';

const InstallationDevices = withScroll(({ installation }) => {
  const devices = mDevice.sortElements(installation.devices, ['name']);

  return (
    <div className="d-flex h-100 align-items-center">
      {_map(devices, (device) => (
        <div key={device.id} className="mr-2">
          <DeviceWidget
            device={device}
            parentId={installation.id}
            source="installation"
            tooltipPermission={{
              needed: permissions.pInstallation.configRead,
              permissionsOrContext: 'installationPermissions',
            }}
            withSimcard
          />
        </div>
      ))}
    </div>
  );
});

InstallationDevices.propTypes = {
  installation: installationDataShape,
};

InstallationDevices.defaultProps = {
  installation: null,
};

const InstallationRow = React.forwardRef(({
  installation, rowSpecs, handleStartLoading,
  asyncOperationsErrorsDays, onDelete,
}, ref) => {
  const { id: installationId } = installation;

  // Handlers
  const handleDelete = () => {
    onDelete(installation);
  };

  return (
    <TableRow
      ref={ref}
      element={installation}
      rowSpecs={rowSpecs}
      actions={[
        <EditIconButton key="bEdit" onClick={handleStartLoading} path={showPath(installationId)} />,
        <OnlyWith
          key="bDelete"
          permissions={{
            needed: permissions.pInstallation.write,
            permissionsOrContext: 'installationPermissions',
          }}
        >
          <DeleteIconButton onClick={handleDelete} />
        </OnlyWith>,
      ]}
    >
      <TableCell className={classnames({ 'table-first-details': rowSpecs.withDetails })}>
        <TruncatedTooltipText>{installation.name}</TruncatedTooltipText>
      </TableCell>
      <TableCell className="hidden-sm-down" noPadding>
        <div style={{ height: TABLE_ROW_HEIGHT }}>
          <InstallationDevices installation={installation} />
        </div>
      </TableCell>
      <TableCell className="table-content-3 hidden-md-down">
        <TruncatedTooltipText>
          {installation.inserted_at && <LocalizedStandardDateNoSeconds value={installation.inserted_at} />}
        </TruncatedTooltipText>
      </TableCell>
      <OnlyWith
        permissions={{
          needed: permissions.pAdmin,
        }}
      >
        <TableCell className="table-content-2 hidden-md-down">
          <div
            className="pl-2"
            style={{
              color:
                statusPalette[
                  mInstallation.asyncOperationsErrorsQuantityLevel(
                    installation.async_operations_error_quantity / asyncOperationsErrorsDays,
                  )
                ],
            }}
          >
            <strong>{installation.async_operations_error_quantity}</strong>
          </div>
        </TableCell>
      </OnlyWith>
      <OnlyWith
        permissions={{
          needed: permissions.pAdmin,
        }}
      >
        <TableCell className="table-content-2 hidden-md-down">
          <div
            className="pl-2"
            style={{
              color:
                statusPalette[
                  mInstallation.asyncOperationsSuccessRate(
                    installation.async_operations_success_rate * 100,
                    installation.async_operations_error_quantity,
                  )
                ],
            }}
          >
            <strong>{`${_round(installation.async_operations_success_rate * 100, 2)}%`}</strong>
          </div>
        </TableCell>
      </OnlyWith>
      <OnlyWith
        permissions={{
          needed: permissions.pAdmin,
        }}
      >
        <TableCell className="hidden-md-down">
          <TruncatedTooltipText>
            {installation.last_success_async_operation && (
              <LocalizedStandardDate value={installation.last_success_async_operation.date} />
            )}
          </TruncatedTooltipText>
        </TableCell>
      </OnlyWith>
      <OnlyWith
        permissions={{
          needed: permissions.pAdmin,
        }}
      >
        <TableCell className="hidden-md-down">
          <TruncatedTooltipText>
            {installation.last_error_async_operation && (
              <LocalizedStandardDate value={installation.last_error_async_operation.date} />
            )}
          </TruncatedTooltipText>
        </TableCell>
      </OnlyWith>
    </TableRow>
  );
});

InstallationRow.displayName = 'InstallationRow';

InstallationRow.propTypes = {
  installation: installationDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
  asyncOperationsErrorsDays: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
};

InstallationRow.defaultProps = {
  onDelete: null,
};

module.exports = compose(
  withLoading,
)(InstallationRow);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/main/InstallationRow.js